<template>
  <div class="ma-6">
    <ORow fill-x>
      <v-btn icon @click="reload"><v-icon>mdi-refresh</v-icon></v-btn>
      <v-btn color="primary" @click="newMailbox">Novo Mailbox...</v-btn>
    </ORow>

    <ODialog ref="detailsDialog" hideWhenLoading>
      <v-card v-if="detailsRow != null">
        <ODialogBar title="Detalhes" @close="$refs.detailsDialog.$dismiss()" />
        <v-card-text class="pa-3 maxh-4">
          <pre class="mono">{{ detailsRow | prettyJson }}</pre>
        </v-card-text>
        <v-card-actions class="pa-6">
          <OHoldToConfirm
            @confirmed="cancelMailbox(detailsRow)"
            label="Cancelar"
            :disabled="!canCancelCommand"
          />
          <v-spacer />
          <OHoldToConfirm
            @confirmed="resendMailbox(detailsRow)"
            label="Reenviar"
          />
          <v-spacer />
          <v-btn text large @click="$refs.detailsDialog.$dismiss()">OK</v-btn>
        </v-card-actions>
      </v-card>
    </ODialog>

    <div class="mt-6">
      <ODataGrid
        v-if="gridData != null"
        class="report-grid dense-table"
        :data="gridData"
        :limit.sync="limit"
        :offset.sync="offset"
        @rowClicked="showRowDetails"
      />
    </div>
  </div>
</template>

<script>
import EditStore from './EditStore'
import { go } from '@/models'
import utils from 'oderon-commons/src/utils.js'
import ODataGrid from 'oderon-vue/cpts/ODataGrid.vue'
import { preProcessSpec } from 'oderon-commons/src/tables'
import grid from 'oderon-commons/src/grid'
import fmt from 'oderon-commons/src/formatters'
import OHoldToConfirm from 'oderon-vue/cpts/OHoldToConfirm.vue'

const WAIT = {
  iconClass: 'orange--text',
  iconText: 'mdi-dots-horizontal-circle'
}
const OK = { iconClass: 'green--text', iconText: 'mdi-checkbox-marked-circle' }
const NOK = { iconClass: 'red--text', iconText: 'mdi-close-circle' }

function statusStyle(x) {
  if (x == 'waiting') return WAIT
  if (x == 'delivered') return OK
  return NOK
}

async function fetchRows({ cameraDef, offset, limit }) {
  if (cameraDef == null || cameraDef.imei == null)
    throw new Error('Falta o IMEI para buscar mailbox items')

  let imei = cameraDef.imei
  let order = 'updatedAt desc'
  let ps = { imei, offset, limit, order }

  if (cameraDef.model == 'jc371') {
    return await go.get(`/cameras/jimi2/camera-mailbox`, ps)
  } else {
    return await go.get('/cameras/jimi1/mailbox', ps)
  }
}

async function fetchRowById({ cameraDef, mailboxId }) {
  if (cameraDef == null)
    throw new Error('Falta CameraDef para buscar mailbox por ID')

  if (cameraDef.model == 'jc371') {
    return await go.get(`/cameras/jimi2/camera-mailbox/${mailboxId}`)
  } else {
    return await go.get(`/cameras/jimi1/mailbox/${mailboxId}`)
  }
}

async function postCommand({ cameraDef, commandStr }) {
  if (cameraDef == null || cameraDef.imei == null)
    throw new Error('Falta o IMEI para enviar comandos')

  if (commandStr == null || commandStr.length < 1)
    throw new Error('Falta o comando a ser enviado')

  let ps = { imei: cameraDef.imei, commandStr }

  if (cameraDef.model == 'jc371') {
    return await go.post('/cameras/jimi2/camera-mailbox', ps)
  } else {
    return await go.post('/cameras/jimi1/mailbox', ps)
  }
}

async function cancelCommand({ cameraDef, mailboxId }) {
  if (cameraDef == null || cameraDef.imei == null)
    throw new Error('Falta o IMEI para enviar comandos')

  if (!Number.isFinite(mailboxId))
    throw new Error('Falta o ID do comando a ser cancelado')

  if (cameraDef.model == 'jc371') {
    return null
  } else {
    return await go.delete(`/cameras/jimi1/mailbox/${mailboxId}`)
  }
}

export default {
  components: { ODataGrid, OHoldToConfirm },
  data() {
    return {
      EditStore,

      adapterSpec: preProcessSpec({
        defaultSortable: false,
        columns: [
          { t: 'ID|', d: 'id', w: 50 },
          {
            t: '|Status|',
            d: 'status',
            w: 150,
            cellStyleFn: (row) => statusStyle(row.status)
          },
          { t: 'Comando', d: 'commandStr', w: 150 },
          { t: 'Resultado', d: 'replyStr', w: 150 },
          {
            t: 'Alterado em|',
            d: 'updatedAt',
            f: fmt.brDateTime,
            w: 180
          }
        ]
      }),

      rows: [],
      count: 0,
      offset: 0,
      limit: 10,

      detailsRow: null
    }
  },
  computed: {
    canCancelCommand() {
      return EditStore.row != null && EditStore.row.model != 'jc371'
    },
    paging() {
      return { limit: this.limit, offset: this.offset }
    },
    currentTab() {
      return EditStore.currentTab
    },
    gridData() {
      return grid.generateWebGrid({
        rows: this.rows,
        spec: this.adapterSpec,
        count: this.count,
        opts: this.paging
      })
    }
  },
  watch: {
    paging() {
      this.reload()
    },
    currentTab: {
      handler(x) {
        if (x == 'mailbox') this.reload()
      },
      immediate: true
    }
  },
  methods: {
    reload() {
      this.$loading(() => this.fetchData())
    },

    async fetchData() {
      const res = await fetchRows({
        cameraDef: EditStore.row,
        limit: this.paging.limit,
        offset: this.paging.offset
      })
      this.rows = res.rows
      this.count = res.count
    },

    async showRowDetails(mailboxItem) {
      this.detailsRow = null
      this.$refs.detailsDialog.$loading(async () => {
        this.detailsRow = await fetchRowById({
          cameraDef: EditStore.row,
          mailboxId: mailboxItem.id
        })
      })

      return this.$refs.detailsDialog.$open()
    },

    async newMailbox() {
      const commandStr = await this.$prompt(
        'Digite o comando a ser enviado para a câmera:'
      )
      if (!utils.isEmpty(commandStr)) {
        await this.$loading(async () => {
          await postCommand({ cameraDef: EditStore.row, commandStr })
          await this.fetchData()
        })

        this.$snack('Comando solicitado com sucesso')
      }
    },

    async cancelMailbox(mailboxItem) {
      await this.$loading(async () => {
        await cancelCommand({
          cameraDef: EditStore.row,
          mailboxId: mailboxItem.id
        })
        await this.fetchData()
      })

      this.$refs.detailsDialog.$dismiss()
      this.$snack('Cancelado com sucesso')
    },

    async resendMailbox(mailboxItem) {
      await this.$loading(async () => {
        let commandStr = mailboxItem.commandStr
        await postCommand({ cameraDef: EditStore.row, commandStr })
        await this.fetchData()
      })

      this.$refs.detailsDialog.$dismiss()
      this.$snack('Comando solicitado com sucesso')
    }
  }
}
</script>

<style></style>
